import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShoppingBag } from "lucide-react";
import { formatINR } from "@/lib/format";
import { StarRating } from "@/components/common/StarRating";
import { WishlistButton } from "@/components/product/WishlistButton";
import { VariantPicker } from "@/components/product/VariantPicker";
import { QuantityStepper } from "@/components/product/QuantityStepper";
import { useCart } from "@/context/CartContext";

function QuickGallery({ product }) {
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [product]);
  return (
    <div className="bg-cream">
      <div className="aspect-square overflow-hidden">
        <img src={product.images?.[active]} alt={product.title} className="h-full w-full object-cover" />
      </div>
      {product.images?.length > 1 && (
        <div className="flex gap-2 p-3">
          {product.images.slice(0, 4).map((img, i) => (
            <button key={img || i} onClick={() => setActive(i)} aria-label={`View image ${i + 1}`} className={`h-14 w-14 overflow-hidden border ${active === i ? "border-gold" : "border-transparent"}`}>
              <img src={img} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function QuickViewModal({ product, open, onOpenChange }) {
  const { addItem } = useCart();
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (product) {
      setVariant(product.variants?.[0] || null);
      setQty(1);
    }
  }, [product]);

  if (!product) return null;

  const handleAdd = () => {
    addItem(product, variant?.id, qty);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden border-border bg-ivory p-0 sm:rounded-none" data-testid="quick-view-modal">
        <DialogTitle className="sr-only">{product.title}</DialogTitle>
        <DialogDescription className="sr-only">Quick view of {product.title}</DialogDescription>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <QuickGallery product={product} />

          <div className="flex flex-col p-6 sm:p-8">
            <p className="text-luxe text-[10px] text-gold">{product.category}</p>
            <h3 className="mt-2 font-serif text-2xl text-ink sm:text-3xl">{product.title}</h3>
            <div className="mt-2">
              <StarRating rating={product.rating} count={product.review_count} size={13} />
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-sans text-lg font-semibold text-ink">{formatINR(product.price)}</span>
              {product.compare_at_price > product.price && (
                <span className="text-sm text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>
              )}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{product.short_description}</p>

            <VariantPicker product={product} value={variant} onChange={setVariant} className="mt-5" testId="quick-view-variant-selector" />

            <div className="mt-6 flex items-center gap-3">
              <QuantityStepper value={qty} onChange={setQty} testIdPrefix="quick-view" />
              <WishlistButton product={product} className="h-12 w-12 border border-border" />
            </div>

            <button
              onClick={handleAdd}
              disabled={!product.available}
              data-testid="quick-view-add-to-cart"
              className="mt-4 flex w-full items-center justify-center gap-2 bg-ink py-4 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold disabled:bg-muted-foreground/60"
            >
              <ShoppingBag size={15} />
              {product.available ? "Add to Bag" : "Sold Out"}
            </button>
            <Link
              to={`/product/${product.handle}`}
              onClick={() => onOpenChange(false)}
              data-testid="quick-view-full-details"
              className="mt-3 text-center text-xs uppercase tracking-widest text-muted-foreground underline-offset-4 hover:text-gold hover:underline"
            >
              View full details
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
